<i18n lang="yaml">
  en:
    btnText1: 'Order'
    btnText2: 'Notify me of availability'
    notAvailable: 'Not available'
    gpu: 'GPU'
    privateNetwork: 'Private network'
    kvm: 'KVM-console'
    day: 'day'
    month: 'month'
  ru:
    btnText1: 'Заказать'
    btnText2: 'Узнать о поступлении'
    notAvailable: 'Временно нет в&nbsp;наличии'
    gpu: 'Графический ускоритель'
    privateNetwork: 'Приватная сеть'
    kvm: 'KVM-консоль'
    day: 'день'
    month: 'мес.'
</i18n>

<template>
  <div class="server-card">
    <div class="server-card__row server-card__row--top">
      <div class="server-card__col">
        <h3 class="server-card__name">
          <nuxt-link :to="localePath(`/services/dedicated/config/?uuid=${server.uuid}`)">
            {{ server.name }}
          </nuxt-link>
        </h3>
      </div>

      <div class="server-card__col server-card__col--tags">
        <div class="server-card__tags">
          <template v-if="hasTags">
            <span
              v-for="tag in tagList"
              :key="tag.uuid"
              :class="['server-card__tag', (tagListDictionary[tag.uuid].key === 'new' || tagListDictionary[tag.uuid].key === 'sale') && 'server-card__tag--colored']"
            >
              <svg-percent v-if="tagListDictionary[tag.uuid].key === 'sale'"></svg-percent>
              {{ tagListDictionary[tag.uuid].name[$i18n.locale] }}
            </span>
          </template>
          <span
            v-if="server.config.gpu"
            class="server-card__tag"
          >
            {{ $t('gpu') }}
          </span>
        </div>
      </div>
      <div class="server-card__col server-card__col--availability">
        <span
          v-if="isOrder"
          class="server-card__quantity-stock"
          v-html="piecesLeft(quantityStock)"
        ></span>
        <span
          v-else
          v-html="$t('notAvailable')"
          class="server-card__unavailable"
        ></span>
      </div>
    </div>

    <div
      class="server-card__row server-card__row--middle"
      :class="{'server-card__row--issale' : isSale}"
    >
      <div class="server-card__col">
        <span class="server-card__cpu-name">
          {{ server.config.cpu.name }}
        </span>

        <span
          class="server-card__cpu-params"
          v-html="server.config.cpu.params">
        </span>
      </div>

      <div class="server-card__col">
        <span v-html="server.config.ram"></span>

        <span v-if="server.config.gpu">
          {{ server.config.gpu }}
        </span>
      </div>

      <div class="server-card__col">
        <span
          v-for="disk in server.config.disk.split(' + ')"
          :key="disk"
        >
          {{ disk }}
        </span>
      </div>

      <div class="server-card__col server-card__col--prices">
        <template v-if="tariffPlan.type === 'month' && tariffPlan.discount">
          <div class="server-card__price server-card__price--row">
            <span
              v-if="isSale && serverOldPrices && isVisibleOldPrice"
              class="server-card__price server-card__price--old"
            >
              {{ $priceFormatter(+serverOldPrices.oldPriceMonth) }}/{{ $t('month') }}
            </span>
            <span
              class="server-card__price"
              :class="{'server-card__price--primary': hasServerOldPrices}"
            >
              {{ $priceFormatter(+monthPrice) }}/{{ $t('month') }}
            </span>
          </div>
          <div class="server-card__price server-card__price--row">
            <span
              v-if="isSale && serverOldPrices && isVisibleOldPrice"
              class="server-card__price server-card__price--old"
            >
              {{ $priceFormatter(+serverOldPrices.oldPriceMonth * tariffPlan.period) }}/{{ tariffPlan.period }}&nbsp;{{ $t('month') }}
            </span>
            <span
              class="server-card__price"
              :class="{'server-card__price--primary': hasServerOldPrices}"
            >
              {{ $priceFormatter(+severalMonthsPrice) }}/{{ tariffPlan.period }}&nbsp;{{ $t('month') }}
            </span>
          </div>
        </template>
        <template v-else>
          <div class="server-card__price server-card__price--row">
            <span
              v-if="serverOldPrices && isVisibleOldPrice"
              class="server-card__price server-card__price--old"
            >
              {{ $priceFormatter(+serverOldPrices.oldPriceMonth) }}/{{ $t('month') }}
            </span>
            <span
              class="server-card__price"
              :class="{'server-card__price--primary': hasServerOldPrices}"
            >
              {{ $priceFormatter(+monthPrice) }}/{{ $t('month') }}
            </span>
          </div>
          <div
            v-if="dayPrice"
            class="server-card__price server-card__price--row"
          >
            <span
              v-if="serverOldPrices && isVisibleOldPrice"
              class="server-card__price server-card__price--old"
            >
              {{ $priceFormatter(+serverOldPrices.oldPriceDay) }}/{{ $t('day') }}
            </span>
            <span
              class="server-card__price"
              :class="{'server-card__price--primary': hasServerOldPrices}"
            >
              {{ $priceFormatter(+dayPrice) }}/{{ $t('day') }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <div class="server-card__row server-card__row--bottom">
      <div class="server-card__col">
        <ul
          class="server-card__feature-list"
        >
          <li
            :class="{ 'server-card__feature-item--crossed': server.tariffLine === TariffLine.CHIPCORE}"
            class="server-card__feature-item server-card__feature-item--lan"
          >
            {{ $t('privateNetwork') }}
          </li>
          <li
            :class="{ 'server-card__feature-item--crossed': server.tariffLine === TariffLine.CHIPCORE && !hasKvm }"
            class="server-card__feature-item server-card__feature-item--console"
          >
            {{ $t('kvm') }}
          </li>
        </ul>
      </div>

      <div class="server-card__col server-card__col--inline">
        <nuxt-link
          v-if="isOrder"
          class="btn btn--sm btn--outline"
          :to="localePath(`/services/dedicated/config/?uuid=${server.uuid}`)"
          data-gtmid="gtm-selectel-server-fix-add-services"
        >
          {{ $t('btnText1') }}
        </nuxt-link>
        <a
          v-else
          class="server-card__admission-btn btn btn--sm btn--outline"
          href="#"
          :data-gtmid="gtmEventAvailability"
          :data-serverName="server.name"
          :data-serverUuid="server.uuid"
          @click.prevent="$emit('showModal', $event)"
        >
          {{ $t('btnText2') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
  import SvgPercent from '~/assets/icons/percent.svg'
  import {serversSale} from '~/helpers/enum/dedicatedServersSale'
  import {TariffLine} from '~/rest-client/servers/all/enum'
  import {translations} from '~/helpers/functions/translations'

  const tagListDictionary = {
    'cfaab26f-478e-4a07-9e2e-99949fc68a11': {
      name: {
        ru: 'New',
        en: 'New'
      },
      key: 'new'
    },
    'b7db45b8-714d-4c56-92dd-d5115eaffc80': {
      name: {
        ru: 'Sale',
        en: 'Sale'
      },
      key: 'sale'
    },
    '23a2110e-cca3-45e6-8d65-04c2cc072cec': {
      name: {
        ru: '2 блока питания',
        en: '2PSU'
      },
      key: '2psu'
    }
  }

  export default {
    name: 'ServerCard',
    components: {
      SvgPercent
    },
    props: {
      server: {
        type: Object,
        required: true
      },
      tariffPlan: {
        type: Object,
        default: () => null
      },
      isSale: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        oldPriceMonth: 34000,
        oldPriceDay: 1700,

        tagListDictionary,
        serversSale,
        TariffLine,
        translations
      }
  },
  computed: {
    serverOldPrices() {
      return serversSale.find(serverSale => serverSale.uuid === this.server.uuid)
    },
    hasServerOldPrices() {
      return !!this.serverOldPrices
    },
    isVisibleOldPrice() {
      return this.oldPriceDay > this.dayPrice && this.oldPriceMonth > this.monthPrice
    },
    quantityStock() {
      return this.server.available.reduce((quantity, available) => {
        quantity += available.count

        return quantity
      }, 0)
    },
    hasTags() {
      return !!this.server.tagList.length
    },
    isOrder() {
      return this.server.isOrder && this.getAvailable(this.server.available)
    },
    tariffPlanDiscountPercentage() {
      return 1 - this.tariffPlan.discount / 100
    },
    monthPrice() {
      const price = this.server.priceCollection.rub.month

      if (this.tariffPlan.discount) {
        return price * this.tariffPlanDiscountPercentage
      }

      return this.server.priceCollection.rub.month
    },
    severalMonthsPrice() {
      const price = this.server.priceCollection.rub.month

      if (this.tariffPlan.type === 'month' && this.tariffPlan.discount) {
        return price * this.tariffPlan.period * this.tariffPlanDiscountPercentage
      }
    },
    dayPrice() {
      return this.server.priceCollection.rub.day
    },
    gtmEventAvailability() {
      const serverName = this.server.name.toLowerCase()

      return serverName === 'cl01'
        ? `gtm-selectel-server-fix-availability-${serverName}`
        : `gtm-selectel-server-fix-availability`
    },
    tagList() {
      return this.server.tagList.filter(tag => {
        if (!tag.isHide) return tag
      }).sort((a, b) => {
        if (a.sortWeight > b.sortWeight) return -1
        if (a.sortWeight < b.sortWeight) return 1
        return 0
      }).filter(item => this.tagListDictionary[item.uuid])
    },
    hasKvm() {
      return !!this.server.tagList.filter(tag => tag.text.toLowerCase() === 'kvm').length > 0
    }
  },
  methods: {
    piecesLeft(count) {
      const names = {
        ru: `Осталось ${count}&nbsp;шт.`,
        en: `${count} pieces left`
      }

      return names[this.$i18n.locale]
    },
    getAvailable(available) {
      return available.reduce((isAvailable, available) => {
        return isAvailable || !!available.count
      }, false)
    }
  }
}
</script>

<style lang="scss">
.server-card {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  padding: 16px;
  border: 1px solid $grayBorder;
  border-radius: 6px;

  @media (max-width: $breakpoint-sm) {
    margin-bottom: 8px;
  }
}

.server-card__row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 20px;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: $breakpoint-lg) {
    grid-template-columns: repeat(2, 1fr);
  }

  &--top {
    grid-template-columns: auto auto 1fr;
    grid-column-gap: 8px;
    align-items: center;
    margin-bottom: 6px;

    @media (max-width: $breakpoint-lg) {
      margin-bottom: 16px;
    }

    @media (max-width: $breakpoint-sm) {
      grid-template-columns: 155px repeat(2, 1fr);
    }

    @media (max-width: $breakpoint-xs) {
      grid-template-columns: 54% repeat(2, 1fr);
    }
  }

  &--middle {
    grid-template-columns: 300px repeat(3, 1fr);
    margin-bottom: 27px;

    @media (max-width: $breakpoint-lg) {
      grid-template-columns: 215px repeat(3, 1fr);
      font-size: 14px;
    }

    @media (max-width: $breakpoint-md) {
      grid-template-columns: 1fr;
      grid-row-gap: 16px;
      margin-bottom: 14px;
      font-size: 16px;
    }
  }

  &--issale {
    grid-template-columns: 24% repeat(2, 1fr) 35%;
    column-gap: 8px;

    @media (max-width: $breakpoint-xl) {
      grid-template-columns: 24% repeat(2, 1fr) 40%;
    }

    @media (max-width: $breakpoint-lg) {
      grid-template-columns: 215px repeat(3, 1fr);
    }

    @media (max-width: $breakpoint-md) {
      grid-template-columns: 1fr;
    }
  }

  &--bottom {
    align-items: center;

    @media (max-width: $breakpoint-md) {
      grid-template-columns: 1fr;
      padding-bottom: 8px;
    }

    .btn {
      @media (max-width: $breakpoint-md) {
        width: 100%;
      }

      &:not(:last-child) {
        margin-right: 8px;

        @media (max-width: $breakpoint-md) {
          margin-right: 0;
          margin-bottom: 8px;
        }
      }
    }
  }
}

.server-card__col {
  display: flex;
  flex-direction: column;

  &:last-child {
    align-items: flex-end;
  }

  &--inline {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
  }

  &--availability {
    grid-column-start: 3;

    @media (max-width: $breakpoint-sm) {
      grid-column: 2 / 5;
    }
  }

  &--title {
    flex-direction: row;
    align-items: center;
  }

  &--tags {
    @media (max-width: $breakpoint-md) {
      grid-column: 1 / 5;
      grid-row-start: 3;
    }
  }

  &--prices:nth-child(n) {
    @media (max-width: $breakpoint-md) {
      align-items: flex-start;
    }
  }
}

.server-card__name {
  margin-bottom: 0;
  font-size: 24px;

  a {
    color: inherit;

    &:hover {
      color: $secondaryDark;
    }
  }
}

.server-card__tags {
  display: inline-flex;
  flex-wrap: wrap;

  @media (max-width: $breakpoint-md) {
    margin-top: 16px;
  }
}

.server-card__tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 5px;
  color: #FFF;
  font-size: 12px;
  line-height: 16px;
  background-color: $primary;
  border-radius: 4px;

  &:not(:last-child) {
    margin-right: 5px;
  }

  &--colored {
    background-color: $secondary;
  }

  svg {
    width: 12px;
    height: 12px;
    margin-right: 8px;
  }
}

.server-card__quantity-stock {
  position: relative;
  padding-left: 16px;
  color: $greenText;
  font-size: 14px;

  @media (max-width: $breakpoint-lg) {
    font-size: 12px;
  }

  &:before {
    content: '';
    position: absolute;
    top: calc(50% - 4px);
    left: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $greenText;
  }
}

.server-card__unavailable {
  color: rgba($primary, 0.6);
  font-size: 14px;
  text-align: right;

  @media (max-width: $breakpoint-lg) {
    font-size: 12px;
  }

  @media (max-width: $breakpoint-md) {
    line-height: 1em;
  }
}

.server-card__feature-list {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 16px;

  @media (max-width: $breakpoint-lg) {
    font-size: 12px;
  }

  @media (max-width: $breakpoint-md) {
    margin-bottom: 16px;
  }
}

.server-card__feature-item {
  position: relative;
  margin-right: 16px;
  padding: 8px 8px 8px 24px;

  &:before {
    content: '';
    position: absolute;
    top: calc(50% - 8px);
    left: 0;
    width: 16px;
    height: 16px;
  }

  &--crossed {
    text-decoration: line-through;
  }

  &--lan:before {
    background-image: url('~@/assets/images/components/common/servers/lan.svg?inline');
  }

  &--console:before {
    background-image: url('~@/assets/images/components/common/servers/console.svg?inline');
  }

  &--gpu:before {
    background-image: url('~@/assets/images/components/common/servers/gpu.svg?inline');
  }
}

.server-card__price {
  white-space: nowrap;

  &--row {
    display: flex;
    align-items: center;

    @media (max-width: $breakpoint-lg) {
      flex-direction: column-reverse;
      align-items: flex-end;

      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }

    @media (max-width: $breakpoint-md) {
      align-items: flex-start;
    }
  }

  &--old {
    margin-right: 16px;
    font-size: 14px;
    text-decoration: line-through;

    @media (max-width: $breakpoint-lg) {
      margin-right: 0;
      font-size: 16px;
    }
  }

  &--primary {
    font-weight: 600;
  }
}
</style>